// Layout — the shell (Design Standard v1, deploy 2): a 56px icon rail, a 56px top
// bar, the page, and on a phone a bottom dock instead of the rail. Nothing floats
// over the page any more: no insights column, no assistant drawer, no collapse
// state. The page owns its width.
import React, { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Rail, { RAIL_WIDE_KEY, readRailWide } from './Rail';
import TopBar from './TopBar';
import Dock from './Dock';
import useShellNav from './shellNav';
import IncomingCallToast from '../Voice/IncomingCallToast';
import ActiveCall from '../Voice/ActiveCall';

import { getSubNavForPage } from '../../constants/uiRegistry';
import { fetchScorecard, withScorecardTab } from '../Analytics/scorecard/scorecardAccess';
import pickActiveTab from './pickActiveTab';
import { getIndustry, getUserRole } from '../../helpers/tenantHelpers';
import { useTheme } from '../../theme/ThemeProvider';

import './shell.css';

// Self-portals into #modal-root. Lane G owns this file; only consumed here.
const CustomerPopup = lazy(() => import('../Profile/CustomerPopup'));

const PHONE_QUERY = '(max-width: 768px)';

function useIsPhone() {
  const [phone, setPhone] = useState(() => (typeof window !== 'undefined' ? window.matchMedia(PHONE_QUERY).matches : false));
  useEffect(() => {
    const mm = window.matchMedia(PHONE_QUERY);
    const handle = () => setPhone(mm.matches);
    mm.addEventListener('change', handle);
    return () => mm.removeEventListener('change', handle);
  }, []);
  return phone;
}

export default function Layout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { mode } = useTheme();
  const phone = useIsPhone();
  const { current } = useShellNav();

  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState(null);

  /* Section tabs: the sub-navigation of the current top-level page. */
  useEffect(() => {
    const seg = location.pathname.split('/').filter(Boolean)[0] || '';
    const basePath = `/${seg}`;
    const fullPath = location.pathname;

    let cancelled = false;
    const recomputeTabs = (scorecardEnabled = false) => {
      const industryKey = getIndustry('general');
      const userRole = getUserRole('Member');
      let availableTabs = getSubNavForPage(basePath, industryKey, userRole) || [];
      // Owner scorecard tab: only on Analytics, only when the backend says this
      // tenant has it (preferences.owner_scorecard.enabled). Hidden otherwise.
      if (basePath === '/analytics') availableTabs = withScorecardTab(availableTabs, scorecardEnabled);
      setTabs(availableTabs);
      const matchedTab = pickActiveTab(availableTabs, fullPath);
      if (!matchedTab && availableTabs.length > 0 && fullPath === basePath) {
        navigate(availableTabs[0].path, { replace: true });
        setActiveTab(availableTabs[0].key);
      } else {
        setActiveTab(matchedTab?.key || null);
      }
    };

    const withScorecard = () => {
      recomputeTabs();
      if (basePath !== '/analytics') return;
      let tenantId = null;
      try { tenantId = JSON.parse(localStorage.getItem('activeTenant') || 'null')?.id ?? null; } catch { /* none */ }
      fetchScorecard(tenantId).then((d) => { if (!cancelled && d?.enabled) recomputeTabs(true); });
    };

    withScorecard();
    const onStorage = (e) => { if (e.key === 'activeTenant' || e.key === 'ui_overrides') withScorecard(); };
    window.addEventListener('storage', onStorage);
    return () => { cancelled = true; window.removeEventListener('storage', onStorage); };
  }, [location, navigate]);

  // The voice device boots silently once per session so the app-global
  // incoming-call toast works without an "Enable Calling" button. No mic
  // prompt here — the SDK asks when a call is actually answered.
  useEffect(() => {
    let tenantId = null;
    try {
      tenantId = JSON.parse(localStorage.getItem('activeTenant') || 'null')?.id || localStorage.getItem('tenantId');
    } catch { /* ignore */ }
    if (tenantId) {
      import('../../services/voiceBootstrap').then(({ bootVoiceSilently }) => bootVoiceSilently(tenantId));
    }
  }, []);

  // The customer a search hit opened, rendered through CustomerPopup below.
  const [searchCustomer, setSearchCustomer] = useState(null);

  // Rail width: words under icons (72px) or beside them (200px); remembered per browser.
  const [railWide, setRailWide] = useState(readRailWide);
  const toggleRailWide = () => setRailWide((v) => {
    try { localStorage.setItem(RAIL_WIDE_KEY, v ? '0' : '1'); } catch { /* private mode */ }
    return !v;
  });

  const handleTabChange = (tabKey) => {
    const target = tabs.find((t) => t.key === tabKey);
    if (target && location.pathname !== target.path) {
      setActiveTab(tabKey);
      navigate(target.path);
    }
  };

  const handleGlobalSearch = useMemo(() => ({ query, path, result }) => {
    const route = result?.route || {};
    if (path) { navigate(path); return; }
    // A CUSTOMER OPENS THE WINDOW, IT DOES NOT NAVIGATE — the same window every
    // other surface opens; CustomerPopup fetches the full record from the id.
    if (result?.result_type === 'customer') {
      const id = route.customer_id || result.id;
      if (id) { setSearchCustomer({ id, object: 'customer', ...result }); return; }
    }
    if (route.web_path) { navigate(route.web_path); return; }
    if (result?.result_type === 'lead' && route.lead_id) { navigate(`/leads/${route.lead_id}`); return; }
    if (result?.result_type === 'quote' && route.quote_id) { navigate(`/quotes/${encodeURIComponent(String(route.quote_id))}`); return; }
    if (query) navigate('/customers/list', { state: { search: query } });
  }, [navigate]);

  return (
    <>
      <div className={`shell ${phone ? 'shell--phone' : ''} ${tabs.length ? 'has-tabs' : ''} ${!phone && railWide ? 'rail-wide' : ''}`} data-theme={mode}>
        {phone ? null : <Rail wide={railWide} onToggleWide={toggleRailWide} />}
        <TopBar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onSearchSelect={handleGlobalSearch}
          sectionLabel={current?.label || ''}
          phone={phone}
        />
        <main className="content-area shell__main">{children}</main>
        {phone ? <Dock /> : null}
      </div>
      {searchCustomer ? (
        <Suspense fallback={null}>
          <CustomerPopup lead={searchCustomer} leadType="customer" onClose={() => setSearchCustomer(null)} />
        </Suspense>
      ) : null}
      {/* A ringing phone must reach you wherever you are — app-level, not
          bound to the Calls page. Renders nothing unless a call is live. */}
      <IncomingCallToast />
      <ActiveCall />
    </>
  );
}
